import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

export const MINIMUM_PANEL_WIDTH = 300;
export const MAXIMUM_PANEL_WIDTH = 500;
export const MINIMUM_PANEL_HEIGHT = 140;
export const MAXIMUM_PANEL_HEIGHT = 800;

const SCROLL_CONTAINER = document.querySelector("#main_container");
const MAIN_CONTAINER = document.querySelector("#main");
const SCROLL_RECT = SCROLL_CONTAINER?.getBoundingClientRect();

const MARGIN = 10;

const shouldBeInLeftSide = (attachedTo) => {
	const attachedToElementRect = attachedTo.getBoundingClientRect();
	return attachedToElementRect.right - SCROLL_RECT.left + SCROLL_CONTAINER.scrollLeft + MAXIMUM_PANEL_WIDTH + MARGIN >= SCROLL_CONTAINER.scrollWidth;
}

// as threadmarkers are related to floating panels, we keep it here
export const createThreadMarker = ({
	attachedTo,
	shape = 'top-triangle',
	onClick,
	isResolved = false,
}) => {
	if (!attachedTo) return null;

	// check if isResolved status changed
	if (attachedTo.dataset.threadResolved) {
		attachedTo.dataset.threadResolved = String(isResolved);
	}

	if (attachedTo.classList.contains("thread-attached") && attachedTo.querySelector(`.thread-shape`)) return attachedTo.querySelector(`.thread-shape`);

	attachedTo.classList.add("thread-attached");

	const shapeElement = document.createElement("span");
	shapeElement.classList.add("thread-shape", `shape-${shape}`);

	if (shouldBeInLeftSide(attachedTo)) {
		shapeElement.classList.add("marker-left");
	} else {
		shapeElement.classList.add("marker-right");
	}

	if (onClick) shapeElement.addEventListener("click", onClick);

	attachedTo.appendChild(shapeElement);

	attachedTo.dataset.threadInitialized = true;
	attachedTo.dataset.threadResolved = isResolved ? true : false;

	return shapeElement;
}

export const destroyThreadMarker = (el) => {
	if (!el) return;
	el.classList.remove("thread-attached");
	const shapeEl = el.querySelector(".thread-shape");
	if (shapeEl) el.removeChild(shapeEl);
	if (el.dataset.threadInitialized) delete el.dataset.threadInitialized;
	return shapeEl;
}



export default function FloatingPanel({
	attachedTo,
	children,
	panelKey,
	width = 500,
	mode
}) {
	const TOTAL_PANEL_WIDTH = width;
	const [openLeftSide, setOpenLeftSide] = useState(false);


	const attachedToElementRect = attachedTo?.getBoundingClientRect();

	useEffect(() => {
		if (attachedTo) {
			setOpenLeftSide(shouldBeInLeftSide(attachedTo));
		}
	}, [attachedTo, SCROLL_RECT]);


	const styleProps =
		attachedToElementRect ?
			openLeftSide
				? {
					left: attachedToElementRect.left - SCROLL_RECT.left + SCROLL_CONTAINER.scrollLeft - TOTAL_PANEL_WIDTH - MARGIN,
					transformOrigin: "top right",
				}
				: {
					left: attachedToElementRect.right - SCROLL_RECT.left + SCROLL_CONTAINER.scrollLeft + MARGIN,
					transformOrigin: "top left",
				} : {};

	return createPortal(
		attachedTo && attachedToElementRect && (
			<motion.div
				key={panelKey || "floating-panel"}
				initial={{ opacity: 0, scale: 0.9, y: -10 }}
				animate={{ opacity: 1, scale: 1, y: 0 }}
				exit={{ opacity: 0, scale: 0.9, y: -10 }}
				transition={{ type: "spring", stiffness: 260, damping: 20 }}
				style={{
					position: "absolute",
					top: attachedToElementRect.top - SCROLL_RECT.top + SCROLL_CONTAINER.scrollTop,
					width: TOTAL_PANEL_WIDTH,
					...styleProps,
				}}
				className="z-50 bg-white shadow-xl rounded-primary border-primary border-normal p-2 text-black text-article min-h-[140px]"
				
			>
				{children}
			</motion.div>
		),
		MAIN_CONTAINER
	);
}